// src/pages/Billing.tsx
import React, { useEffect, useState } from "react";
import { useAuth } from "providers/authProvider";
import { subscriptionAPI } from "../utils/API";

import "../styles/Billing.css"; // ← Import the stylesheet you created
import { Link } from "react-router-dom";
import { pageNames } from "config/pageNames";
import { SubscriptionOptions } from "common/components/subscription/SubscriptionOptions";
import { SubscriptionStatus } from "billing/types/Subscriptions";
import { formatAmount, formatDate } from "billing/utils/formatter";


export const Billing: React.FC = () => {
  const { user } = useAuth();
  const email = user?.email ?? null;

  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subscription = status?.subscription ?? null;
  const isSubscribed = !!status?.isPaying && !!subscription;

  // Fetch subscription when email is ready
  useEffect(() => {
    if (!email) return;

    let mounted = true;

    const fetchStatus = async () => {
      setLoading(true);
      setError(null);
      setStatus(null);

      try {
        const res = await subscriptionAPI.getSubscriptionStatus(email);
        if (mounted) setStatus(res);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Unable to fetch subscription status.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchStatus();
    return () => {
      mounted = false;
    };
  }, [email]);

  //  const isSubscribed = !!status?.isPaying;

  return (
    <div className="billing-page">
      <div className="billing-container">

        {/* HEADER */}
        <header className="billing-header">
          <div className="billing-header-main">
            <p className="billing-header-kicker">Account</p>
            <h1 className="billing-header-title">Billing & Subscription</h1>
            <p className="billing-header-subtitle">
              Review your current plan, manage billing, and unlock premium features when you’re ready.
            </p>
          </div>

          <div className="billing-header-user">
            <p className="billing-header-user-label">Signed in as</p>
            <p className="billing-header-user-email">
              {email ?? "Loading account…"}
            </p>
          </div>
        </header>

        {/* GRID LAYOUT */}
        <div className="billing-grid">

          {/* LEFT CARD — Overview */}
          <section className="billing-card billing-card--overview">

            {/* Title Row */}
            <div className="billing-section-title-row">
              <div>
                <p className="billing-section-label">Current plan</p>
                <div className="flex items-center gap-2">
                  <h2 className="billing-section-title">
                    {isSubscribed ? "Premium" : "Free"}
                  </h2>
                  {isSubscribed && (
                    <span className="billing-chip billing-chip--active">Active</span>
                  )}
                </div>
              </div>

              <div className="billing-status-text">
                <p className="billing-status-label">Status</p>
                <p className="billing-status-value">
                  {loading
                    ? "Checking…"
                    : error
                    ? "Error"
                    : isSubscribed
                    ? "Subscribed"
                    : "Not subscribed"}
                </p>
              </div>
            </div>

            {/* NO EMAIL */}
            {!email && (
              <p className="text-sm text-gray-700">
                Loading your account… Once we have your email, we’ll check your subscription.
              </p>
            )}

            {/* LOADING */}
            {email && loading && (
              <p className="text-sm text-gray-700">Checking subscription status…</p>
            )}

            {/* ERROR */}
            {!loading && error && (
              <div className="billing-highlight billing-highlight--warning">
                <p className="billing-highlight-title">Something went wrong</p>
                <p className="billing-highlight-body">{error}</p>
              </div>
            )}

            {/* SUBSCRIBED */}
            {!loading && !error && isSubscribed && subscription && (
              <div className="billing-stack">
                <div className="billing-highlight billing-highlight--success">
                  <p className="billing-highlight-title">You’re on Premium 🎉</p>
                  <p className="billing-highlight-body">
                    Thanks for supporting the platform — all premium features are enabled.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="billing-chip billing-chip--pill">Unlimited events</span>
                  <span className="billing-chip billing-chip--pill">Advanced analytics</span>
                  <span className="billing-chip billing-chip--pill">Priority support</span>
                </div>

                {/* New: subscription details */}
                <div className="billing-details">
                  <p className="billing-section-label">Subscription details</p>
                  <div className="billing-details-grid">
                    <div className="billing-details-row">
                      <span className="billing-details-label">Amount</span>
                      <span className="billing-details-value">
                        {formatAmount(subscription.price.amount, subscription.price.currency)}{" "}
                        / {subscription.price.interval}
                      </span>
                    </div>

                    <div className="billing-details-row">
                      <span className="billing-details-label">Subscription start</span>
                      <span className="billing-details-value">
                        {formatDate(subscription.startDate)}
                      </span>
                    </div>

                    <div className="billing-details-row">
                      <span className="billing-details-label">Current period</span>
                      <span className="billing-details-value">
                        {formatDate(subscription.currentPeriodStart)} –{" "}
                        {formatDate(subscription.currentPeriodEnd)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* NOT SUBSCRIBED */}
            {!loading && !error && status && !status.isPaying && (
              <div className="billing-stack">
                <div className="billing-highlight billing-highlight--warning">
                  <p className="billing-highlight-title">You’re on the Free plan</p>
                  <p className="billing-highlight-body">
                    Upgrade to unlock the full feature set, improved workflows, and premium capabilities.
                  </p>
                </div>

                {/* Plan Comparison */}
                <div className="billing-plans-grid">
                  <div className="billing-plan-card billing-plan-card--free">
                    <p className="billing-plan-title">Free</p>
                    <p>Basic access, limited actions and reporting tools.</p>
                  </div>

                  <div className="billing-plan-card billing-plan-card--premium">
                    <p className="billing-plan-title">Premium</p>
                    <p>Unlimited events, deeper insights, collaboration tools, priority support.</p>
                  </div>
                </div>
              </div>
            )}

            {/* NO STATUS RETURNED */}
            {!loading && !error && email && !status && (
              <p className="text-sm text-gray-700">
                We couldn’t determine your subscription status. You may still subscribe using the button on the right.
              </p>
            )}

          </section>

          {/* RIGHT CARD — CTA */}
          <aside className="billing-card billing-card--cta">
            <p className="billing-section-label">Subscription</p>

            {email ? (
              isSubscribed ? (
                <>
                  <p className="text-sm text-gray-800">
                    Your subscription is linked to <strong>{email}</strong>.
                  </p>
                  
                  {subscription && (
                    <p className="billing-footnote">
                      {formatAmount(subscription.price.amount, subscription.price.currency)}{" "}
                      billed every {subscription.price.interval}, next renewal on{" "}
                      {formatDate(subscription.currentPeriodEnd)}.
                    </p>
                  )}

                  <Link to={pageNames.BILLING_MANAGEMENT}>
                      <button className="billing-button billing-button--outline">Manage subscription</button>
                  </Link>

                </>
              ) : (
                <>
                  <p className="text-sm text-gray-800">
                    Ready to upgrade? Start a subscription with{" "}
                    <strong>{email}</strong>.
                  </p>

                  <SubscriptionOptions email={email} />

                  <p className="billing-footnote">
                    You will be redirected to a secure Stripe Checkout page.
                  </p>
                </>
              )
            ) : (
              <p className="text-sm text-gray-700">Loading your account…</p>
            )}

          </aside>
        </div>

        <p className="billing-footnote">
          Need help with billing? Contact support anytime.
        </p>

      </div>
    </div>
  );
};

export default Billing;
